/**
 * Converts a number to a string with 2 decimal places.
 * @param {number} num - The number to convert.
 * @returns {string} The number formatted as a string with 2 decimal places.
 */
export const formatToTwoDecimals = (num: number) => {
  if (typeof num !== "number") {
    console.warn("formatToTwoDecimals: Input is not a number");
    return "0.00";
  }
  return num.toFixed(2);
};

/**
 * Converts a UTC date string to a regular date format (MM/DD/YYYY).
 * @param {string} utcDate - The UTC date string to convert.
 * @returns {string} The date in MM/DD/YYYY format.
 */
export const formatUTCToMMDDYYYY = (dateString: string): string => {
  const date = new Date(dateString);

  const month = String(date.getUTCMonth() + 1).padStart(2, "0"); // Months are zero-indexed
  const day = String(date.getUTCDate()).padStart(2, "0");
  const year = date.getUTCFullYear();

  return `${month}/${day}/${year}`;
};

export const formatUTCDateTime = (dateString: string): string => {
  const date = new Date(dateString);

  const options: Intl.DateTimeFormatOptions = {
    month: "2-digit",
    day: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
    timeZone: "UTC",
  };

  return date.toLocaleString("en-US", options);
};

// Utility function to shuffle arrays
export const shuffleArray = <T>(array: T[]): T[] => {
  const shuffled = [...array];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
};

export const formatMacAddressWithColons = (macAddress: string): string => {
  // Remove any non-alphanumeric characters first
  const cleanMac = macAddress.replace(/[^A-Fa-f0-9]/g, "");

  // Limit to 12 characters (a valid MAC address length)
  const truncatedMac = cleanMac.slice(0, 12);

  // Insert colons after every 2 characters, except for the last group
  const formattedMac = truncatedMac.match(/.{1,2}/g)?.join(":") || truncatedMac;

  return formattedMac.toUpperCase();
};

/**
 * Finds the discounted product in an array of  products.
 * @param {array} products - The array of internet products.
 * @returns {object} The discounted product object.
 */

export const formatDeviceName = (name: string): string => {
  // Allow letters, numbers, hyphens, underscores, and spaces
  return name.replace(/[^a-zA-Z0-9\-_\s]/g, "");
};

export const formatErrorMsg = (error: string) => {
  let errorValue = error.replace(/"/g, "");
  errorValue = errorValue.replace(/;/g, ",");

  function capitalize(text: string) {
    var firstChar = text.charCodeAt(0);
    if (firstChar >= 97 && firstChar <= 122) {
      return String.fromCharCode(firstChar - 32) + text.substr(1);
    }
    return text;
  }
  errorValue = errorValue.trim();
  errorValue = capitalize(errorValue);
  return errorValue;
};

interface FormattedProduct {
  name: string;
  price: number;
  quantity?: number;
}

export const convertProductCodesToReadableNames = (
  productCodes: string[]
): FormattedProduct[] => {
  const products: FormattedProduct[] = [];
  const additionalDeviceCount = productCodes.filter((code) =>
    code.includes("DL_Broadband_Additional_Device_[1_Month_Recurring]")
  ).length;

  // Process internet plans first
  const internetPlan = productCodes.find((code) => {
    return (
      code.includes("Broadband_Internet") ||
      code.includes("_Broadband_Internet_") ||
      code.includes("Fiber") ||
      code.includes("Ethernet")
    );
  });

  if (internetPlan) {
    const priceMatch = internetPlan.match(/\d+\.\d+(?=\])/);
    const price = priceMatch ? parseFloat(priceMatch[0]) : 0;
    const duration = extractDurationFromProductCode(internetPlan);

    let planName = "";
    if (internetPlan.includes("Blazing")) {
      planName = "Blazing Internet";
    } else if (internetPlan.includes("Extreme")) {
      planName = "Extreme Internet";
    } else if (internetPlan.includes("Extra")) {
      planName = "Extra Internet";
    } else if (internetPlan.includes("Fiber_1Gig")) {
      planName = "Fiber 1Gig Internet";
    } else if (internetPlan.includes("Ethernet_1Gig")) {
      planName = "Ethernet 1Gig Internet";
    }

    if (planName) {
      products.push({
        name: `${planName} (${duration})`,
        price: price,
      });
    }
  }

  // Process additional devices
  if (additionalDeviceCount > 0) {
    products.push({
      name: `${additionalDeviceCount} Additional Device${
        additionalDeviceCount > 1 ? "s" : ""
      } (Monthly)`,
      price: additionalDeviceCount * 10.0,
      quantity: additionalDeviceCount,
    });
  }

  return products;
};

/**
 * Extracts the duration value from a product code string.
 * @param {string} productCode - The product code string to extract duration from.
 * @returns {string} The duration value (Day, Week, Month, or Monthly).
 */
export const extractDurationFromProductCode = (productCode: string): string => {
  // Match pattern between [1_ and either _ or ] including cases with Recurring and price
  const match = productCode.match(
    /\[1_(Day|Week|Month|Monthly)(?:_Recurring(?:_\d+\.\d+)?|\]|_\d+\.\d+\])/
  );

  if (!match) return "";

  const duration = match[1];
  const hasRecurring = productCode.includes("Recurring");

  // If it has Recurring and duration is Month, return Monthly
  if (hasRecurring && duration.toLowerCase() === "month") {
    return "Monthly";
  }

  // For all other cases, return "1" + duration
  return `1 ${duration.charAt(0).toUpperCase() + duration.slice(1)}`;
};

/**
 * Appends duration from product code to action label
 * @param {string} label - The original action label
 * @param {string} productCode - The product code to extract duration from
 * @returns {string} The label with appended duration if applicable
 */
export const appendDurationToLabel = (
  label: string,
  productCode?: string
): string => {
  if (!productCode) return label;

  const duration = extractDurationFromProductCode(productCode);

  // Only append for "Add Another" action
  if (label === "Add Another" && duration) {
    return `${label} ${duration}`;
  }

  return label;
};

/**
 * Shuffles cancellation reasons and returns the appropriate ones for the plan type
 * @param {object} reasons - Object containing all cancellation reason arrays
 * @param {string} planType - The plan type (BROADBAND_FIBER, BROADBAND_FTTH, etc.)
 * @param {string[]} productCodes - Array of product codes
 * @returns {array} The appropriate shuffled cancellation reasons for the specific plan type
 */
export const getShuffledCancellationReasons = (
  reasons: {
    BB_CANCELLATION_REASONS: any[];
    FIBER_CANCELLATION_REASONS: any[];
    FTTH_CANCELLATION_REASONS: any[];
    FREE_PLAN_CANCELLATION_REASONS: any[];
  },
  planType?: string,
  productCodes?: string[]
) => {
  // Determine which reasons to use based on plan type
  let reasonsToShuffle;

  if (["BROADBAND_FIBER", "BROADBAND_FTTH"].includes(planType || "")) {
    // Handles FTTH and FIBER plans
    reasonsToShuffle =
      planType === "BROADBAND_FIBER"
        ? reasons.FIBER_CANCELLATION_REASONS
        : reasons.FTTH_CANCELLATION_REASONS;
  } else if (
    productCodes?.find((product: string) =>
      product.toLowerCase().includes("free")
    )
  ) {
    // Handles FREE plans (AETC, etc.)
    reasonsToShuffle = reasons.FREE_PLAN_CANCELLATION_REASONS;
  } else {
    // Handles regular BROADBAND plans
    reasonsToShuffle = reasons.BB_CANCELLATION_REASONS;
  }

  // Shuffle and return
  return shuffleArray(reasonsToShuffle);
};

/**
 * Determines message content from messeage-center.const.ts based on product code and message data
 * @param {string | undefined} internetProductCode - The internet product code
 * @param {object | undefined} messageData - The message data object
 * @param {Record<string, any>} messagesIndex - The messages index object
 * @returns {object | null} The appropriate message content or null
 */
export const determineMessageContent = (
  internetProductCode?: string,
  messageData?: { code: string },
  messagesIndex?: Record<string, any>,
  customerData?: any
): any => {
  if (!messagesIndex) return null;
  if (customerData?.status === "ACTIVE" && !customerData?.deferred_actions) {
    if (internetProductCode?.includes("AETC_Free")) {
      return messagesIndex["FREE_RESTRICTED_AETC"];
    }
    if (internetProductCode?.includes("DL_Army")) {
      return messagesIndex["FREE_RESTRICTED_ARMY"];
    }
    // Check for PAID_UNRESTRICTED_AETC condition
    if (
      customerData?.history_has_aetc_free &&
      !customerData?.has_free_wifi_product
    ) {
      return messagesIndex["PAID_UNRESTRICTED_AETC"];
    }

    if (
      customerData?.history_has_army_free &&
      !customerData?.has_free_wifi_product
    ) {
      return messagesIndex["PAID_UNRESTRICTED_ARMY"];
    }
  } 
  if (
    // Check for inactive basic broadband free plan
    customerData?.status === "INACTIVE" &&
    internetProductCode?.includes("DL_Basic_Broadband_Internet")
  ) {
    return messagesIndex["ACCOUNT_INACTIVE"];
  }
  return messagesIndex[messageData?.code || "NONE"];
  
};

/**
 * Converts currency code to currency symbol
 * @param {string} currencyCode - The currency code (e.g., "BRL", "USD", "EUR")
 * @returns {string} The currency symbol (e.g., "R$", "$", "€")
 */
export const currencyConverter = (currencyCode?: string): string => {
  if (!currencyCode) return "$";

  switch (currencyCode.toUpperCase()) {
    case "BRL":
      return "R$";
    case "USD":
      return "$";
    case "CAD":
      return "C$";
    case "EUR":
      return "€";
    case "GBP":
      return "£";
    case "JPY":
    case "CNY":
      return "¥";
    case "NOK":
      return "kr";
    default:
      return "$"; // Default to USD
  }
};
